import { ChangeEvent, useState } from "react"

import { AgencyTeam, GlobalTeam, MarketTeam } from "./types"
import { css, cx } from "@emotion/css"

import { EmptyState } from "components/EmptyState"
import { Search } from "../components/Search"
import Surface from "components/Surface"
import { Table } from "components/tables"
import { ReactComponent as TeamsIcon } from "assets/icon-team.svg"
import { columns } from "./columns"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"

type Props<T> = {
  data?: T[]
  isLoading: boolean
  onDelete: (id: string) => void
  onEdit: (id: string) => void
  onOpen?: VoidFunction
  onClose?: VoidFunction
  hasColumnsReordering?: boolean
  newMember?: () => void
}

export function TeamMembers<T extends { name: string }>(props: Props<T>) {
  const { t } = useI18n()
  const { hasAuthority } = useAuthenticated()
  const {
    data,
    isLoading = false,
    onDelete,
    onEdit,
    onOpen,
    hasColumnsReordering,
    newMember,
  } = props
  const [searchValue, setSearchValue] = useState<string>("")

  const handleSearchChange = (
    evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value } = evt.target
    setSearchValue(() => value)
  }

  const handleSearchClear = () => {
    setSearchValue(() => "")
  }

  const filteredValue =
    data?.length && !!searchValue
      ? data?.filter((d) =>
          d?.name.toLowerCase().includes(searchValue.toLowerCase()),
        )
      : data
  const filters = [] as any
  const noData = !data?.length

  return (
    <>
      <Surface mt={0}>
        <div
          className={cx(styles.container, { [styles.noDataContainer]: noData })}
        >
          <Table<GlobalTeam | MarketTeam | AgencyTeam>
            id="supplier-groups-list-table"
            columns={columns(t, onDelete, hasAuthority, onEdit)}
            data={filteredValue ?? []}
            filters={filters ?? []}
            total={data?.length || 0}
            loading={isLoading}
            hasColumnsReordering={hasColumnsReordering}
            localSearch={
              <LocalSearch
                searchValue={searchValue}
                handleSearchChange={handleSearchChange}
                handleSearchClear={handleSearchClear}
              />
            }
            onOpen={onOpen}
            isPaginated={false}
            noResults={
              <EmptyState
                icon={<TeamsIcon />}
                title={t("teams.noresults.title")}
                subtitle={
                  hasAuthority(["AUTH_CREATE_USER_MARKET"])
                    ? t("teams.noresults.subtitle")
                    : undefined
                }
                className={styles.emptyStateWidth}
              >
                {hasAuthority(["AUTH_CREATE_USER_MARKET"]) ? (
                  <span
                    onClick={newMember}
                    className={cx(
                      "BodyBody-1Primary-mainRegular",
                      styles.createNew,
                    )}
                  >
                    {t("teams.create_new")}
                  </span>
                ) : null}
              </EmptyState>
            }
          />
        </div>
      </Surface>
    </>
  )
}

type SearchProps = {
  searchValue: string
  handleSearchChange: (
    evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void
  handleSearchClear: VoidFunction
}

export const LocalSearch = (searchProps: SearchProps) => {
  const { searchValue, handleSearchChange, handleSearchClear } = searchProps
  const { t } = useI18n()
  return (
    <Search
      name={"name"}
      value={searchValue || ""}
      handleChange={handleSearchChange}
      handleClear={handleSearchClear}
      placeholder={t("inputs.search.placeholder")}
    />
  )
}

const styles = {
  container: css`
    margin-top: var(--spacing-m);
  `,
  noDataContainer: css`
    margin-top: 0px;
  `,
  emptyStateDiv: css`
    padding-left: var(--spacing-xxl-2);
  `,
  emptyStateWidth: css`
    width: 100%;
  `,
  createNew: css`
    padding-top: 1px;
    margin: 0 0.8ch;
    cursor: pointer;
  `,
}
